import * as React from "react";

import { Menu, MenuItem } from "@mui/material";
import { ButtonProps } from "@mui/material/Button";
import { unstable_useId as useId } from "@mui/utils";
import { gridClasses, useGridApiContext } from "@mui/x-data-grid-pro";
import {
  bindMenu,
  bindTrigger,
  usePopupState,
} from "material-ui-popup-state/hooks";
import { MenuButton } from "../Buttons";
import {
  DROPDOWN_MENU_BG,
  NAV_DROPDOWN_HOVER_BG,
  NAV_DROPDOWN_HOVER_TEXT,
  DROPDOWN_MENU_TEXT,
  dropdownMenuListSx,
  dropdownMenuPaperSx,
} from "../dropdownMenuStyles";

/**
 * ⋮ export / Display Preferences menu — same edge→hover gap as nav/ACTIONS:
 * paper px: 1 + item mx: 0 (pill stretches inside the paper inset). Upstream f470a68ef.
 */
const exportMenuPaperSx = {
  ...dropdownMenuPaperSx,
  backgroundColor: `${DROPDOWN_MENU_BG} !important`,
  bgcolor: `${DROPDOWN_MENU_BG} !important`,
  "& .MuiList-root": {
    ...dropdownMenuListSx,
    backgroundColor: `${DROPDOWN_MENU_BG} !important`,
    py: 0.5,
  },
  "& .MuiMenuItem-root": {
    fontSize: "15px !important",
    fontFamily: '"Roboto-Medium", "Roboto", sans-serif !important',
    fontWeight: "400 !important",
    color: `${DROPDOWN_MENU_TEXT} !important`,
    py: 0.5,
    pl: 1.5,
    pr: 3,
    // Nav/ACTIONS pattern — gap from paper px: 1, not item margin
    mx: 0,
    my: 0.125,
    minHeight: "32px !important",
    height: "32px !important",
    lineHeight: 1.25,
    borderRadius: "8px",
    minWidth: "unset !important",
    width: "100%",
    maxWidth: "100%",
    alignSelf: "stretch",
    boxSizing: "border-box",
    "&:hover": {
      backgroundColor: `${NAV_DROPDOWN_HOVER_BG} !important`,
      color: `${NAV_DROPDOWN_HOVER_TEXT} !important`,
    },
    "&.Mui-selected": {
      backgroundColor: `${NAV_DROPDOWN_HOVER_BG} !important`,
      color: `${NAV_DROPDOWN_HOVER_TEXT} !important`,
    },
    "&.Mui-selected:hover": {
      backgroundColor: `${NAV_DROPDOWN_HOVER_BG} !important`,
      color: `${NAV_DROPDOWN_HOVER_TEXT} !important`,
    },
  },
  "& .list-subheader-item, & .MuiListSubheader-root": {
    backgroundColor: `${DROPDOWN_MENU_BG} !important`,
    color: "#000000 !important",
    fontSize: "15px !important",
    fontFamily: '"Roboto-Bold", "Roboto", sans-serif !important',
    lineHeight: "28px",
    minHeight: 28,
    height: 28,
    py: 0,
    // Align with item text (paper px: 1 + pl: 1.5)
    px: 1.5,
    mx: 0,
    my: 0,
    opacity: "1 !important",
    cursor: "default",
    position: "static",
    "& b, & strong": {
      fontFamily: "inherit !important",
      fontWeight: "normal !important",
    },
    "&:hover": {
      backgroundColor: `${DROPDOWN_MENU_BG} !important`,
    },
  },
  "& .MuiDivider-root": {
    my: 0.5,
    mx: 0,
    borderColor: "rgba(0, 0, 0, 0.08)",
  },
};

/**
 * The toolbar ⋮ menu itself — the list's MenuButton and the house dropdown paper —
 * with NO grid context, so the Pro list toolbar and the Premium detail-tab toolbar
 * (H233) render the SAME menu. Pro (6.x) and Premium (7.x) do not share a React grid
 * context (PATTERNS BP-80): anything here that called `useGridApiContext` would throw
 * inside one of them. Each adapter supplies the accessible label from its own grid.
 *
 * Children are cloned with `hideMenu` (MUI X's export items and our own Settings items
 * call it), so each item must be a DIRECT child — a Fragment wrapper hides them.
 */
export const ToolbarKebabMenu = React.forwardRef<
  HTMLButtonElement,
  ButtonProps & { label: string }
>(function ToolbarKebabMenu(props, ref) {
  const { children, onClick, label, ...other } = props;

  const exportButtonId = useId();
  const exportMenuId = useId();

  const popupState = usePopupState({
    variant: "popover",
    popupId: "export-menu",
  });

  if (children == null) {
    return null;
  }

  const menuBind = bindMenu(popupState);
  const triggerBind = bindTrigger(popupState);

  return (
    <React.Fragment>
      {/* @figma Pattern 430 · 3 Dot Dropdown sits in a 20px-padded frame: 38px
          after SEARCH (its 5px + the container's 8px + 25px) and 50px from the right edge (2px + the
          toolbar's 48px). */}
      <MenuButton
        aria-label={label}
        aria-haspopup="menu"
        id={exportButtonId}
        sx={{ marginLeft: { xs: 1, sm: "25px" }, marginRight: { sm: "2px" } }}
        {...triggerBind}
        {...other}
        ref={ref}
        // The caller's onClick used to be destructured and dropped (upstream f470a68ef).
        onClick={(event) => {
          triggerBind.onClick(event);
          onClick?.(event);
        }}
      />
      <Menu
        {...menuBind}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          elevation: 0,
          sx: exportMenuPaperSx,
        }}
        MenuListProps={{
          id: exportMenuId,
          className: gridClasses.menuList,
          "aria-labelledby": exportButtonId,
          dense: true,
          disablePadding: true,
          sx: {
            ...dropdownMenuListSx,
            backgroundColor: `${DROPDOWN_MENU_BG} !important`,
            py: 0.5,
          },
        }}
      >
        {React.Children.map(children, (child) => {
          if (!React.isValidElement(child)) {
            return child;
          }
          return React.cloneElement<any>(child, {
            hideMenu: popupState.close,
          });
        })}
      </Menu>
    </React.Fragment>
  );
});

/** The list toolbar's ⋮ (Pro grid): the shared menu, labelled from the Pro grid's locale. */
export const GridToolbarExportContainer = React.forwardRef<
  HTMLButtonElement,
  ButtonProps
>(function GridToolbarExportContainer(props, ref) {
  const apiRef = useGridApiContext();
  return (
    <ToolbarKebabMenu
      ref={ref}
      label={apiRef.current.getLocaleText("toolbarExportLabel")}
      {...props}
    />
  );
});

/**
 * Settings → "Display": opens the grid's Display Preferences dialog. Shared by the
 * list ⋮ and the Premium detail-tab ⋮ so the two menus read alike. It consumes the
 * injected `hideMenu` so the ⋮ closes behind the dialog (PATTERNS
 * `settingsMenuExtras`, trap 2) and never forwards it to the DOM.
 */
export function DisplaySettingsMenuItem({
  hideMenu,
  onOpen,
}: {
  hideMenu?: () => void;
  onOpen: () => void;
}) {
  return (
    <MenuItem
      onClick={() => {
        hideMenu?.();
        onOpen();
      }}
    >
      Display
    </MenuItem>
  );
}
